import * as React from "react";
import {
  List,
  DataTable,
  TextField,
  EditButton,
  ReferenceField,
  ReferenceInput,
  ChipField,
  ReferenceArrayField,
  SingleFieldList,
  ReferenceArrayInput,
  SelectArrayInput,
  BooleanInput,
  SearchInput,
  AutocompleteInput,
  usePermissions,
  SelectInput,
  SelectField,
  TopToolbar,
  CreateButton,
  ExportButton,
  FilterButton,
  Button,
  useListContext,
  useDataProvider,
  useNotify,
  useRefresh,
  Confirm,
  LinearProgress,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import QueueIcon from "@mui/icons-material/Queue";

import { ROLE_TYPES } from "../enums/Roles";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { PriceField } from "../common/PriceField";
import {
  getProductStatusOptionsByRole,
  PRODUCT_TYPES_OPTIONS,
} from "../utils/ProductUtils";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { Archive, Check, Close } from "@mui/icons-material";

const ProductFilter = () => {
  const { permissions } = usePermissions();
  return [
    <SearchInput source="name" alwaysOn />,
    <SelectArrayInput
      label="Estado"
      source="status"
      choices={getProductStatusOptionsByRole(permissions)}
      validate={(value) =>
        !value || value.length === 0
          ? "Selecciona al menos un estado"
          : undefined
      }
      alwaysOn
    />,
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <ReferenceInput
            source="restaurant"
            reference="restaurants"
            perPage={100}
            filter={{
              type: RESTAURANT_TYPES.REGULAR,
            }}
            label="Restaurante"
            alwaysOn
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ name: searchText })}
              label="Restaurante"
              optionText="name"
            />
          </ReferenceInput>,
        ]
      : []),
    <SelectInput label="Tipo" source="type" choices={PRODUCT_TYPES_OPTIONS} />,
    <ReferenceArrayInput
      source="categories"
      reference="categories"
      perPage={100}
      label="Categorías"
    >
      <SelectArrayInput optionText="name" label="Categorías" />
    </ReferenceArrayInput>,
    <BooleanInput
      label="Restaurante Activo"
      source="restaurantActive"
      defaultValue={true}
    />,
  ];
};

const ProductListActions = () => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      <FilterButton />
      <CreateButton />
      {permissions === ROLE_TYPES.ADMIN && (
        <Button
          label="Crear múltiples"
          onClick={() => {
            navigate("/products/bulk-create");
          }}
        >
          <QueueIcon />
        </Button>
      )}
      <ExportButton />
    </TopToolbar>
  );
};

// Centralized status configuration
const STATUS_CONFIG = {
  [ENTITY_AVAILABILITY_STATUS.ACTIVE]: {
    icon: <Check />,
    message: "activados",
    errorMessage: "Ha ocurrido un error al activar los productos",
    actionLabel: "Activar",
    confirmTitle: "Activar",
    confirmContent: "¿Estás seguro de activar los productos seleccionados?",
    loadingMessage: "Activando productos...",
  },
  [ENTITY_AVAILABILITY_STATUS.INACTIVE]: {
    icon: <Close />,
    message: "desactivados",
    errorMessage: "Ha ocurrido un error al desactivar los productos",
    actionLabel: "Desactivar",
    confirmTitle: "Desactivar",
    confirmContent: "¿Estás seguro de desactivar los productos seleccionados?",
    loadingMessage: "Desactivando productos...",
  },
  [ENTITY_AVAILABILITY_STATUS.ARCHIVED]: {
    icon: <Archive />,
    message: "archivados",
    errorMessage: "Ha ocurrido un error al archivar los productos",
    actionLabel: "Archivar",
    confirmTitle: "Archivar",
    confirmContent: "¿Estás seguro de archivar los productos seleccionados?",
    loadingMessage: "Archivando productos...",
  },
};

const ProductBulkActionButtons = () => {
  // Centralized state management
  const [confirmStates, setConfirmStates] = React.useState({
    [ENTITY_AVAILABILITY_STATUS.ACTIVE]: false,
    [ENTITY_AVAILABILITY_STATUS.INACTIVE]: false,
    [ENTITY_AVAILABILITY_STATUS.ARCHIVED]: false,
  });
  const [isLoading, setIsLoading] = React.useState(false);

  const { selectedIds, onUnselectItems } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Generic handler for all status changes
  const onBulkStatusClick = (status) => {
    if (!selectedIds?.length) return;
    setConfirmStates((prev) => ({ ...prev, [status]: true }));
  };

  // Generic function to close confirmation dialogs
  const closeConfirmDialog = (status) => {
    setConfirmStates((prev) => ({ ...prev, [status]: false }));
  };

  const handleBulksStatusConfirm = (status) => () => {
    if (!selectedIds?.length) return;
    const { loadingMessage, message, errorMessage } = STATUS_CONFIG[status];

    notify(loadingMessage);
    setIsLoading(true);

    return dataProvider
      .updateStatus("products", {
        productIds: selectedIds,
        status: status,
      })
      .then(({ data }) => {
        const success = data?.success;
        const successResults = data?.successResults;

        notify(
          `${successResults || 0} productos ${message} con éxito. ${
            !success
              ? "Algunos productos no han sido procesados correctamente"
              : ""
          }`,
          { type: success ? "success" : "warning" }
        );
        onUnselectItems();
        refresh();
      })
      .catch((error) => {
        notify(`${errorMessage}: ${error?.body?.message || error.message}`, {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        closeConfirmDialog(status);
      });
  };

  return (
    <>
      {isLoading ? (
        <LinearProgress />
      ) : (
        Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <Button
            key={status}
            label={config.actionLabel}
            onClick={() => onBulkStatusClick(status)}
            disabled={isLoading}
          >
            {config.icon}
          </Button>
        ))
      )}

      {Object.entries(STATUS_CONFIG).map(([status, config]) => (
        <Confirm
          key={status}
          isOpen={confirmStates[status]}
          title={`${config.confirmTitle} ${selectedIds?.length} productos`}
          content={config.confirmContent}
          onConfirm={handleBulksStatusConfirm(status)}
          onClose={() => closeConfirmDialog(status)}
          loading={isLoading}
        />
      ))}
    </>
  );
};

const ProductList = () => {
  const { permissions } = usePermissions();
  return (
    <List
      title="Productos"
      filters={ProductFilter()}
      actions={<ProductListActions />}
      filterDefaultValues={{
        status: [
          ENTITY_AVAILABILITY_STATUS.ACTIVE,
          ENTITY_AVAILABILITY_STATUS.INACTIVE,
        ],
      }}
    >
      <DataTable
        rowClick="show"
        bulkActionButtons={
          permissions === ROLE_TYPES.ADMIN && <ProductBulkActionButtons />
        }
      >
        <DataTable.Col source="name" label="Nombre" />
        <DataTable.Col source="price" label="Precio">
          <PriceField
            source="price"
            country="restaurantData.country"
            label="Precio"
          />
        </DataTable.Col>
        {permissions === ROLE_TYPES.ADMIN && (
          <DataTable.Col source="restaurant" label="Restaurante">
            <ReferenceField
              label="Restaurante"
              reference="restaurants"
              source="restaurant"
              link={false}
            >
              <TextField source="name" label="Nombre" />
            </ReferenceField>
          </DataTable.Col>
        )}
        <DataTable.Col source="categories" label="Categorías">
          <ReferenceArrayField
            label="Categorías"
            reference="categories"
            source="categories"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
        </DataTable.Col>
        <DataTable.Col source="type" label="Tipo">
          <SelectField
            label="Tipo"
            source="type"
            choices={PRODUCT_TYPES_OPTIONS}
          />
        </DataTable.Col>
        <DataTable.Col source="status" label="Estado">
          <SelectField
            source="status"
            label="Estado"
            choices={getProductStatusOptionsByRole(permissions)}
          />
        </DataTable.Col>
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

export default ProductList;
